import { getNetworkErrorMessage } from '@framework/auth';
import { StaticInput, useField } from '@framework/form';
import { useTranslation } from '@framework/i18n';
import { Alert } from 'antd';
import { get, isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { RichTextAdvancedRender } from '../../../../../inputs/rich-text-advanced/render/RichTextAdvancedRender';
import { ConfigFormItem } from '../../../../../ui/form/ConfigFormItem';
import { useAsyncService } from '../../../../expressions/repository/async/useAsyncService';
import { useAsyncServiceGetData } from '../../../../expressions/repository/async/useAsyncServiceGetData';
import { useWidgetScopeContext } from '../../../../form/fillable/WidgetScopeContext';
import { useWidgetVisibility } from '../../../hooks/useWidgetVisibility';
import { IWidgetComponentProps } from '../../../IWidgetType';
import { getAsyncServiceLabel } from '../async-select/components/label/getSelectedValueLabel';
import { getWidgetProps } from '../common/util/getWidgetProps';
import { asyncValueWidgetObjectName } from './AsyncValueWidget.type';
import { IAsyncValueWidget } from './IAsyncValueWidget';

export interface IAsyncValueWidgetComponentProps
  extends IWidgetComponentProps<IAsyncValueWidget> {}

export function AsyncValueWidgetComponent(
  props: IAsyncValueWidgetComponentProps,
) {
  const { widget } = props;

  const options = getWidgetProps(props);
  const { prefix } = useWidgetScopeContext();
  const { service, axiosClient } = useAsyncService(widget.serviceId);
  const { t } = useTranslation();
  const visible = useWidgetVisibility(props.widget, options);

  // Faccio la chiamata
  const { data, error, loading, calledOnce } = useAsyncServiceGetData({
    basePath: prefix,
    client: axiosClient,
    service,
    inputs: widget.inputs,
    disabled: options.readonly || !visible,
  });

  const objectFieldName = useMemo(
    () => `${prefix}${asyncValueWidgetObjectName(widget.code)}`,
    [prefix, widget.code],
  );

  const valueField = useField(options.name);
  const objectField = useField(objectFieldName);
  const [label, setLabel] = useState(
    getAsyncServiceLabel(widget.labelFields, objectField.getValue()),
  );

  useEffect(() => {
    if (options.readonly) return;
    // Se la chiamata non è stata fatta, non faccio nulla (mantengo i valori originali)
    if (!calledOnce.current) return;
    if (loading || error) return;

    // Se è presente più di un elemento, ritorno il primo
    const singleValues = Array.isArray(data) ? data[0] : data;

    if (widget.outputs == null) return;

    // Casto i values in base allo schema dell'oggetto così da renderlo uguale al
    // valore al momento salvato a livello di struttura, chiavi e tipi, così da poterli anche confrontare.
    // e.g. se per esempio gli output non sono identici ai value (a livello di chiavi)
    // i check successivi sono considerati tutti falsi andando poi a chiamare
    // la .setValue() sui field, rendendo il form dirty senza motivo
    const values = objectField.schema
      .at(0)
      ?.cast(singleValues, { assert: false });

    const currentObjectValue = objectField.getValue();

    // Se è già null e i valori sono null, non faccio nulla
    if (currentObjectValue === null && values == null) {
      return;
    }

    // Se l'oggetto salvato non è null e deve essere annullato, lo annullo
    if (currentObjectValue !== null && values == null) {
      valueField.setValue(null);
      setLabel('');
      objectField.setValue({});
      return;
    }

    // Se non ci sono differenze tra i valori, non faccio nulla
    if (isEqual(values, currentObjectValue)) {
      return;
    }

    // Se ci sono differenze, vado a impostare value e object
    const newValue = get(values, widget.valueField);

    // Questo widget è readonly, come i widget calcolati. Se cambia non dovrebbe influenzare
    // lo stato del form. L'unico momento in cui una sua modifica dovrebbe influenzare il form
    // è nel caso un suo parametro venga modificato (per la query) ma quei parametri sono dinamici
    // solo in caso di reference ad altri widget, che a loro volta renderebbero _comunque_ il
    // form dirty, rendendo quindi inutile questa operazione.
    // In questo modo, non andiamo a rendere dirty il form al primo render di una nuova sezione
    // (quando il campo passa da undefined a un valore ritornato come "default" dal servizio, nel caso)
    valueField.setValue(newValue, { shouldDirty: false, shouldTouch: false });
    objectField.setValue(values, { shouldDirty: false, shouldTouch: false });

    // Andiamo a costruire la label con tutti i campi configurati
    setLabel(getAsyncServiceLabel(widget.labelFields, values));
  }, [
    calledOnce,
    data,
    error,
    loading,
    objectField,
    valueField,
    widget.labelFields,
    widget.valueField,
    widget.outputs,
  ]);

  if (error) {
    return (
      <Alert
        message={getNetworkErrorMessage(
          error,
          t(
            'async-value-widget.error-message',
            'Si è verificato un errore, riprova più tardi',
          ),
        )}
        type="error"
      />
    );
  }

  return (
    <ConfigFormItem
      hidden={widget.layout?.hide}
      name={options.name}
      label={<RichTextAdvancedRender inline text={options.label} />}
      help={<RichTextAdvancedRender inline text={options?.helpText} />}
      layout={{ hideVisualLabel: options.hideLabel }}
      readonly={options.readonly}
    >
      <StaticInput
        value={label}
        removeBorder={widget.layout?.removeBorder}
        loading={loading}
      />
    </ConfigFormItem>
  );
}
